import { composeRefs, FaroIconButton, FaroMenu, FaroMenuProps, FaroOverlayTrigger } from '@faro/design-system';
import { DownloadIcon, MoreHorizontalIcon, RenameIcon, Trash2Icon } from '@faro/icons';
import { AgenticArtifactReference } from '@faro/study-space-service-model';
import { ReactNode, Ref } from 'react';
import { useStudySpacePermissions } from '../../package/react-hooks/useStudySpacePermissions';
import { StudySpaceAnalyticEventKey, useStudySpaceAnalytics } from '../../util/analytics';
import styles from './PublishedAgenticArtifactTable.module.scss';
import { useDownloadPublishedAgenticArtifact } from './useDownloadPublishedAgenticArtifact';

export interface PublishedAgenticArtifactMenuProps {
    spaceId: string;
    artifact: AgenticArtifactReference;
    onRename(): void;
    onDelete(): void;
    menuButtonRef?: Ref<HTMLButtonElement>;
}

export function PublishedAgenticArtifactMenu(props: PublishedAgenticArtifactMenuProps): ReactNode {
    const { spaceId, artifact, onRename, onDelete, menuButtonRef } = props;
    const { editor } = useStudySpacePermissions();
    const { trackEvent } = useStudySpaceAnalytics();
    const { download, isPending } = useDownloadPublishedAgenticArtifact(spaceId, artifact.artifactId, artifact.version);

    const handleDownload = (): void => {
        trackEvent(StudySpaceAnalyticEventKey.downloadPublishedReport, {
            publishedReportId: artifact.artifactId,
            publishedReportVersion: artifact.version,
            downloadLocation: 'published reports table',
        });
        download();
    };

    return (
        <FaroOverlayTrigger
            placement="bottom-end"
            openOnClick
            strategy="fixed"
            overlay={(menuProps: FaroMenuProps) => (
                <FaroMenu {...menuProps}>
                    {editor && (
                        <FaroMenu.Item left={<RenameIcon />} onClick={onRename}>
                            Rename
                        </FaroMenu.Item>
                    )}
                    <FaroMenu.Item left={<DownloadIcon />} onClick={handleDownload} disabled={isPending}>
                        Download
                    </FaroMenu.Item>
                    {editor && (
                        <FaroMenu.Item color="danger" left={<Trash2Icon />} onClick={onDelete}>
                            Delete
                        </FaroMenu.Item>
                    )}
                </FaroMenu>
            )}
        >
            {(triggerProps, state) => (
                <FaroIconButton
                    {...triggerProps}
                    ref={composeRefs(triggerProps.ref, menuButtonRef)}
                    icon={<MoreHorizontalIcon />}
                    appearance="inline"
                    active={state.open}
                    className={`${styles.menuButton} ${state.open ? styles.menuOpen : ''}`}
                    data-testid="publishedAgenticArtifactMenuButton"
                />
            )}
        </FaroOverlayTrigger>
    );
}
